<script setup lang="ts">
import type { LoadingSize } from './use-loading'

defineOptions({ name: 'MsLoading' })

// The screen that stands in for the application while it is starting.

withDefaults(
  defineProps<{
    /** Covers the viewport, for the moment before there is an application. */
    fullscreen?: boolean
    /** The wash from the static screen in index.html. */
    waves?: boolean
    size?: LoadingSize
    /** What a screen reader is told while this is up. */
    label?: string
  }>(),
  { fullscreen: false, waves: false, size: 'default', label: 'Loading' },
)
</script>

<template>
  <div
    class="ms-loading"
    :class="[`ms-loading--${size}`, { 'ms-loading--fullscreen': fullscreen }]"
    role="status"
  >
    <span v-if="waves && fullscreen" class="ms-loading__wash" aria-hidden="true">
      <span class="ms-loading__wave ms-loading__wave--one" />
      <span class="ms-loading__wave ms-loading__wave--two" />
    </span>
    <span class="ms-loading__stage">
      <span class="ms-loading__tile" aria-hidden="true" />
      <span v-if="$slots.default" class="ms-loading__mark"><slot /></span>
    </span>
    <span class="ms-loading__label">{{ label }}</span>
  </div>
</template>

<style scoped lang="scss">
.ms-loading {
  display: flex;
  align-items: center;
  justify-content: center;

  &--fullscreen {
    position: fixed;
    inset: 0;
    z-index: var(--loading-z-index);
    background: var(--loading-backdrop);
  }

  &__wash {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  }

  &__wave {
    position: absolute;
    inset-block-start: var(--loading-wave-offset-block);
    inset-inline-start: var(--loading-wave-offset-inline);
    inline-size: var(--loading-wave-size);
    block-size: var(--loading-wave-size);
    border-radius: var(--loading-wave-radius);
    background: var(--loading-wave-color);
    opacity: var(--loading-wave-alpha-one);
    animation: ms-loading-drift var(--loading-wave-duration-one) var(--ease-linear) infinite;

    &--two {
      inset-inline-start: var(--loading-wave-offset-inline-two);
      opacity: var(--loading-wave-alpha-two);
      animation-duration: var(--loading-wave-duration-two);
    }
  }

  &__stage {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    inline-size: var(--loading-size);
    block-size: var(--loading-size);
  }

  &__tile {
    position: absolute;
    inset: 0;
    border-radius: var(--loading-radius);
    background: var(--loading-tile-bg);
    animation: ms-loading-turn var(--loading-duration) var(--loading-ease) infinite;
  }

  &__mark {
    position: relative;
    display: inline-flex;
    color: var(--loading-mark-fg);
    inline-size: var(--loading-mark-size);
    block-size: var(--loading-mark-size);

    > :deep(*) {
      inline-size: 100%;
      block-size: 100%;
    }

    :deep(svg),
    :deep(img) {
      inline-size: 100%;
      block-size: 100%;
    }
  }

  &__label {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  &--sm {
    --loading-size: var(--loading-size-sm);
    --loading-mark-size: var(--loading-mark-size-sm);
    --loading-radius: var(--loading-radius-sm);
  }

  &--lg {
    --loading-size: var(--loading-size-lg);
    --loading-mark-size: var(--loading-mark-size-lg);
  }
}

/* Half a turn, a pause, half a turn, a pause — the legacy's timing, kept. */
@keyframes ms-loading-turn {
  0% {
    transform: rotate(0deg);
  }

  25%,
  50% {
    transform: rotate(180deg);
  }

  75%,
  100% {
    transform: rotate(360deg);
  }
}

/* One turn, and the second keyframe is `to`. */
@keyframes ms-loading-drift {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Something still has to say "working". */
@media (prefers-reduced-motion: reduce) {
  /* The wash is decoration; still is fine for it. */
  .ms-loading__wave {
    animation: none;
  }

  .ms-loading__tile {
    animation: ms-loading-breathe var(--loading-duration) var(--ease-in-out) infinite;
  }

  @keyframes ms-loading-breathe {
    0%,
    100% {
      opacity: 1;
    }

    50% {
      opacity: var(--loading-breathe-opacity);
    }
  }
}
</style>
